import {
    ActionIcon,
    Button,
    Group,
    NumberInput,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { CityQuestCipherConfig } from '@routellers/types/dist/city-quest/city-quest-content';
import {
    CityQuestArHuntMechanics,
    CityQuestArHuntTexts,
    CityQuestSensorMechanics,
    CityQuestSensorTexts,
} from '@routellers/types/dist/city-quest/city-quest-parts';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { enumOptions } from '../../i18n';

/**
 * Формы механик точки, зависящих от challengeType. Значения хранятся и
 * патчатся целыми объектами (spread поверх загруженного) — неизвестные
 * поля json-конфигов при редактировании не теряются.
 */

const SENSOR_KINDS = ['blow', 'shake', 'tilt', 'compass'] as const;
const CIPHER_KINDS = ['caesar', 'symbol_map'] as const;

/** Правила из city-quest-play.utils.ts (checkDynamicAnswer). */
const DYNAMIC_ANSWER_RULES = [
    'server_compares_submitted_time_to_local_time',
    'server_accepts_current_hour_figure_number_on_12h_cycle',
] as const;

type SensorConfigProps = {
    value: CityQuestSensorMechanics | null;
    onChange: (value: CityQuestSensorMechanics | null) => void;
};

export const SensorConfigFields = ({ value, onChange }: SensorConfigProps) => {
    const { t } = useTranslation();
    const kindOptions = useMemo(() => enumOptions(SENSOR_KINDS, t, 'enums.sensorKind'), [t]);

    return (
        <>
            <Select
                label={t('editors.quest.constructor.challengeMechanics.sensorKind')}
                data={kindOptions}
                clearable
                value={value?.kind || null}
                onChange={(v) =>
                    onChange(
                        v
                            ? { ...(value || {}), kind: v as CityQuestSensorMechanics['kind'] }
                            : null,
                    )
                }
            />
            {value && value.kind !== 'compass' && (
                <NumberInput
                    label={t('editors.quest.constructor.challengeMechanics.sensorDuration')}
                    min={1}
                    value={value.durationSec ?? ''}
                    onChange={(v) =>
                        onChange({ ...value, durationSec: typeof v === 'number' ? v : undefined })
                    }
                />
            )}
            {value?.kind === 'compass' && (
                <Group grow>
                    <NumberInput
                        label={t(
                            'editors.quest.constructor.challengeMechanics.sensorTargetHeading',
                        )}
                        min={0}
                        max={359}
                        value={value.targetHeadingDeg ?? ''}
                        onChange={(v) =>
                            onChange({
                                ...value,
                                targetHeadingDeg: typeof v === 'number' ? v : undefined,
                            })
                        }
                    />
                    <NumberInput
                        label={t('editors.quest.constructor.challengeMechanics.sensorTolerance')}
                        min={1}
                        max={180}
                        value={value.toleranceDeg ?? ''}
                        onChange={(v) =>
                            onChange({
                                ...value,
                                toleranceDeg: typeof v === 'number' ? v : undefined,
                            })
                        }
                    />
                </Group>
            )}
        </>
    );
};

type SensorTextsProps = {
    value: CityQuestSensorTexts | null;
    onChange: (value: CityQuestSensorTexts) => void;
};

export const SensorTextsFields = ({ value, onChange }: SensorTextsProps) => {
    const { t } = useTranslation();

    return (
        <>
            <TextInput
                label={t('editors.quest.constructor.stopTexts.sensorActionLabel')}
                value={value?.actionLabel || ''}
                onChange={(e) =>
                    onChange({ ...(value || {}), actionLabel: e.currentTarget.value })
                }
            />
            <TextInput
                label={t('editors.quest.constructor.stopTexts.sensorSuccessText')}
                value={value?.successText || ''}
                onChange={(e) =>
                    onChange({ ...(value || {}), successText: e.currentTarget.value })
                }
            />
        </>
    );
};

type CipherConfigProps = {
    value: CityQuestCipherConfig | null;
    onChange: (value: CityQuestCipherConfig | null) => void;
};

export const CipherConfigFields = ({ value, onChange }: CipherConfigProps) => {
    const { t } = useTranslation();
    const kindOptions = useMemo(() => enumOptions(CIPHER_KINDS, t, 'enums.cipherKind'), [t]);
    const mapEntries = Object.entries(value?.symbol_map || {});

    const patchMapEntry = (index: number, symbol: string, letter: string) => {
        if (!value) return;

        const next = mapEntries.map((entry, i) => (i === index ? [symbol, letter] : entry));

        onChange({ ...value, symbol_map: Object.fromEntries(next) });
    };

    return (
        <>
            <Select
                label={t('editors.quest.constructor.stopTexts.cipher.kind')}
                data={kindOptions}
                clearable
                value={value?.kind || null}
                onChange={(v) =>
                    onChange(
                        v
                            ? {
                                  ciphertext: '',
                                  ...(value || {}),
                                  kind: v as CityQuestCipherConfig['kind'],
                              }
                            : null,
                    )
                }
            />
            {value && (
                <>
                    <Textarea
                        label={t('editors.quest.constructor.stopTexts.cipher.ciphertext')}
                        minRows={2}
                        autosize
                        value={value.ciphertext}
                        onChange={(e) => onChange({ ...value, ciphertext: e.currentTarget.value })}
                    />
                    {value.kind === 'caesar' && (
                        <>
                            <NumberInput
                                label={t('editors.quest.constructor.stopTexts.cipher.shift')}
                                value={value.shift ?? ''}
                                onChange={(v) =>
                                    onChange({
                                        ...value,
                                        shift: typeof v === 'number' ? v : undefined,
                                    })
                                }
                            />
                            <TextInput
                                label={t('editors.quest.constructor.stopTexts.cipher.alphabet')}
                                description={t(
                                    'editors.quest.constructor.stopTexts.cipher.alphabetHint',
                                )}
                                value={value.alphabet || ''}
                                onChange={(e) =>
                                    onChange({
                                        ...value,
                                        alphabet: e.currentTarget.value || undefined,
                                    })
                                }
                            />
                        </>
                    )}
                    {value.kind === 'symbol_map' && (
                        <Stack gap='xs'>
                            <Text size='sm' fw={600}>
                                {t('editors.quest.constructor.stopTexts.cipher.symbolMap')}
                            </Text>
                            {mapEntries.map(([symbol, letter], index) => (
                                <Group key={index} gap='xs' wrap='nowrap'>
                                    <TextInput
                                        size='xs'
                                        label={t(
                                            'editors.quest.constructor.stopTexts.cipher.symbol',
                                        )}
                                        value={symbol}
                                        onChange={(e) =>
                                            patchMapEntry(index, e.currentTarget.value, letter)
                                        }
                                        style={{ width: 110 }}
                                    />
                                    <TextInput
                                        size='xs'
                                        label={t(
                                            'editors.quest.constructor.stopTexts.cipher.letter',
                                        )}
                                        value={letter}
                                        onChange={(e) =>
                                            patchMapEntry(index, symbol, e.currentTarget.value)
                                        }
                                        style={{ width: 110 }}
                                    />
                                    <ActionIcon
                                        color='red'
                                        variant='subtle'
                                        mt={20}
                                        onClick={() =>
                                            onChange({
                                                ...value,
                                                symbol_map: Object.fromEntries(
                                                    mapEntries.filter((_, i) => i !== index),
                                                ),
                                            })
                                        }
                                    >
                                        <IconTrash size={14} />
                                    </ActionIcon>
                                </Group>
                            ))}
                            <Button
                                size='xs'
                                variant='light'
                                leftSection={<IconPlus size={14} />}
                                onClick={() =>
                                    onChange({
                                        ...value,
                                        symbol_map: Object.fromEntries([...mapEntries, ['', '']]),
                                    })
                                }
                            >
                                {t('editors.quest.constructor.stopTexts.cipher.addPair')}
                            </Button>
                            <NumberInput
                                label={t(
                                    'editors.quest.constructor.stopTexts.cipher.revealedPairs',
                                )}
                                min={0}
                                value={value.revealed_pairs ?? ''}
                                onChange={(v) =>
                                    onChange({
                                        ...value,
                                        revealed_pairs: typeof v === 'number' ? v : undefined,
                                    })
                                }
                            />
                        </Stack>
                    )}
                </>
            )}
        </>
    );
};

type ArHuntConfigProps = {
    value: CityQuestArHuntMechanics | null;
    onChange: (value: CityQuestArHuntMechanics) => void;
    /** Координаты по умолчанию для нового символа (обычно — точка квеста). */
    defaultCenter?: { lat: number; lng: number } | null;
};

export const ArHuntConfigFields = ({ value, onChange, defaultCenter }: ArHuntConfigProps) => {
    const { t } = useTranslation();
    const symbols = value?.symbols || [];

    const patchSymbol = (
        index: number,
        patch: Partial<CityQuestArHuntMechanics['symbols'][number]>,
    ) => {
        onChange({
            ...(value || { symbols: [] }),
            symbols: symbols.map((symbol, i) => (i === index ? { ...symbol, ...patch } : symbol)),
        });
    };

    return (
        <>
            <NumberInput
                label={t('editors.quest.constructor.challengeMechanics.arHunt.collectRadius')}
                min={1}
                value={value?.collectRadiusM ?? ''}
                onChange={(v) =>
                    onChange({
                        ...(value || { symbols: [] }),
                        collectRadiusM: typeof v === 'number' ? v : undefined,
                    })
                }
            />
            <Stack gap='xs'>
                <Text size='sm' fw={600}>
                    {t('editors.quest.constructor.challengeMechanics.arHunt.symbols')}
                </Text>
                {symbols.map((symbol, index) => (
                    <Stack
                        key={index}
                        gap={4}
                        p='xs'
                        style={{
                            border: '1px solid var(--mantine-color-gray-3)',
                            borderRadius: 'var(--mantine-radius-sm)',
                        }}
                    >
                        <Group gap='xs' wrap='nowrap' align='flex-end'>
                            <TextInput
                                size='xs'
                                label={t(
                                    'editors.quest.constructor.challengeMechanics.arHunt.symbolId',
                                )}
                                value={symbol.id}
                                onChange={(e) => patchSymbol(index, { id: e.currentTarget.value })}
                                style={{ width: 90 }}
                            />
                            <TextInput
                                size='xs'
                                label={t(
                                    'editors.quest.constructor.challengeMechanics.arHunt.glyph',
                                )}
                                value={symbol.glyph || ''}
                                onChange={(e) =>
                                    patchSymbol(index, {
                                        glyph: e.currentTarget.value || undefined,
                                    })
                                }
                                style={{ width: 70 }}
                            />
                            <NumberInput
                                size='xs'
                                label={t(
                                    'editors.quest.constructor.challengeMechanics.arHunt.lat',
                                )}
                                decimalScale={6}
                                value={symbol.lat}
                                onChange={(v) =>
                                    patchSymbol(index, { lat: typeof v === 'number' ? v : 0 })
                                }
                                style={{ width: 120 }}
                            />
                            <NumberInput
                                size='xs'
                                label={t(
                                    'editors.quest.constructor.challengeMechanics.arHunt.lng',
                                )}
                                decimalScale={6}
                                value={symbol.lng}
                                onChange={(v) =>
                                    patchSymbol(index, { lng: typeof v === 'number' ? v : 0 })
                                }
                                style={{ width: 120 }}
                            />
                            <NumberInput
                                size='xs'
                                label={t(
                                    'editors.quest.constructor.challengeMechanics.arHunt.radius',
                                )}
                                min={1}
                                value={symbol.collectRadiusM ?? ''}
                                onChange={(v) =>
                                    patchSymbol(index, {
                                        collectRadiusM: typeof v === 'number' ? v : undefined,
                                    })
                                }
                                style={{ width: 90 }}
                            />
                            <ActionIcon
                                color='red'
                                variant='subtle'
                                onClick={() =>
                                    onChange({
                                        ...(value || { symbols: [] }),
                                        symbols: symbols.filter((_, i) => i !== index),
                                    })
                                }
                            >
                                <IconTrash size={14} />
                            </ActionIcon>
                        </Group>
                        <TextInput
                            size='xs'
                            label={t(
                                'editors.quest.constructor.challengeMechanics.arHunt.spriteUrl',
                            )}
                            value={symbol.spriteUrl || ''}
                            onChange={(e) =>
                                patchSymbol(index, { spriteUrl: e.currentTarget.value || null })
                            }
                        />
                        <TextInput
                            size='xs'
                            label={t(
                                'editors.quest.constructor.challengeMechanics.arHunt.anchorImageUrl',
                            )}
                            description={t(
                                'editors.quest.constructor.challengeMechanics.arHunt.anchorImageUrlHint',
                            )}
                            value={symbol.anchorImageUrl || ''}
                            onChange={(e) =>
                                patchSymbol(index, {
                                    anchorImageUrl: e.currentTarget.value || null,
                                })
                            }
                        />
                    </Stack>
                ))}
                <Button
                    size='xs'
                    variant='light'
                    leftSection={<IconPlus size={14} />}
                    onClick={() =>
                        onChange({
                            ...(value || {}),
                            symbols: [
                                ...symbols,
                                {
                                    id: `s${symbols.length + 1}`,
                                    lat: defaultCenter?.lat ?? 0,
                                    lng: defaultCenter?.lng ?? 0,
                                },
                            ],
                        })
                    }
                >
                    {t('editors.quest.constructor.challengeMechanics.arHunt.addSymbol')}
                </Button>
            </Stack>
        </>
    );
};

type ArHuntTextsProps = {
    value: CityQuestArHuntTexts | null;
    onChange: (value: CityQuestArHuntTexts) => void;
    /** id символов из механики точки — по одному target hint на символ. */
    symbolIds: string[];
};

export const ArHuntTextsFields = ({ value, onChange, symbolIds }: ArHuntTextsProps) => {
    const { t } = useTranslation();

    return (
        <>
            <TextInput
                label={t('editors.quest.constructor.stopTexts.arHuntSuccessText')}
                value={value?.successText || ''}
                onChange={(e) =>
                    onChange({ ...(value || {}), successText: e.currentTarget.value })
                }
            />
            {symbolIds
                .filter(Boolean)
                .map((symbolId) => (
                    <TextInput
                        key={symbolId}
                        label={t('editors.quest.constructor.stopTexts.arHuntTargetHint', {
                            id: symbolId,
                        })}
                        value={value?.targetHints?.[symbolId] || ''}
                        onChange={(e) =>
                            onChange({
                                ...(value || {}),
                                targetHints: {
                                    ...(value?.targetHints || {}),
                                    [symbolId]: e.currentTarget.value,
                                },
                            })
                        }
                    />
                ))}
        </>
    );
};

type DynamicAnswerProps = {
    rule: string | null;
    tolerance: string | null;
    onChange: (patch: { dynamicAnswerRule?: string | null; dynamicAnswerTolerance?: string | null }) => void;
};

export const DynamicAnswerFields = ({ rule, tolerance, onChange }: DynamicAnswerProps) => {
    const { t } = useTranslation();
    const ruleOptions = useMemo(() => {
        const options = enumOptions(DYNAMIC_ANSWER_RULES, t, 'enums.dynamicAnswerRule');

        // Правило из БД, не входящее в известный список, не должно молча
        // пропадать из селекта.
        if (rule && !options.some((option) => option.value === rule)) {
            return [...options, { value: rule, label: rule }];
        }

        return options;
    }, [t, rule]);

    return (
        <>
            <Select
                label={t('editors.quest.constructor.challengeMechanics.dynamicRule')}
                description={t('editors.quest.constructor.challengeMechanics.dynamicRuleHint')}
                data={ruleOptions}
                clearable
                value={rule}
                onChange={(v) => onChange({ dynamicAnswerRule: v || null })}
            />
            <TextInput
                label={t('editors.quest.constructor.challengeMechanics.dynamicTolerance')}
                description={t(
                    'editors.quest.constructor.challengeMechanics.dynamicToleranceHint',
                )}
                value={tolerance || ''}
                onChange={(e) =>
                    onChange({ dynamicAnswerTolerance: e.currentTarget.value.trim() || null })
                }
            />
        </>
    );
};
